import React, { useState, useMemo, useRef } from 'react';
import { gapValues } from '../utils/productionPlanForecast';

// Production Plan grid row (React.memo) + inline day-cell editor. Aggregation and cell rendering only;
// state, fetching and the column model live in ProductionPlanPage.js.

const num = x => Number(x) || 0;
const fmt = n => n.toLocaleString('en-US');

// ── Editable day cell input: local state, commits once (Enter/Tab/blur), Esc cancels ──
const EditInput = ({ initial, onDone }) => {
  const [v, setV] = useState(initial);
  const done = useRef(false);
  const finish = (val, move) => { if (done.current) return; done.current = true; onDone(val, move); };
  return (
    <input
      type="number" min="0" inputMode="numeric" className="pp-input" value={v} autoFocus
      onFocus={e => e.target.select()}
      onChange={e => setV(e.target.value)}
      onBlur={() => finish(v, null)}
      onKeyDown={e => {
        if (e.key === 'Enter') { e.preventDefault(); finish(v, 'down'); }
        else if (e.key === 'Tab') { e.preventDefault(); finish(v, e.shiftKey ? 'prev' : 'next'); }
        else if (e.key === 'Escape') finish(null, null);
      }}
    />
  );
};

// ── Product row: aggregates its sparse plan/done maps once, renders cells by column kind.
// fc (computeForecast) and cumPlan (Σplan − Σdone cumulative by month) come precomputed from the page (rowCalc). ──
export const Row = React.memo(function Row({ p, planRow, doneRow, commentRow, cols, cal, fc, cumPlan, editing, onEdit, onEditDone, onComment }) {
  const agg = useMemo(() => {
    const mp = {}, md = {}, wp = {}, wd = {};
    const add = (src, byM, byW) => {
      for (const [d, q] of Object.entries(src || {})) {
        const ix = cal.dayIndex[d];
        if (!ix) continue;
        byM[ix.m] = (byM[ix.m] || 0) + num(q);
        byW[ix.w] = (byW[ix.w] || 0) + num(q);
      }
    };
    add(planRow, mp, wp);
    add(doneRow, md, wd);
    return { mp, md, wp, wd };
  }, [planRow, doneRow, cal]);

  const numTd = (i, v, cls = '') => <td key={i} className={v < 0 ? cls + ' pp-neg' : cls}>{fmt(Math.round(v) || 0)}</td>;   // pp-neg by the exact value
  // AI cell: plan; an elapsed period (current month, through yesterday) adds ' / remaining' (= plan − actual sales), red when < 0
  const aiTd = (i, v, rem, elapsed, act, cls) => {
    if (v == null) return <td key={i} className={cls}>—</td>;
    if (!elapsed) return <td key={i} className={cls}>{fmt(Math.round(v))}</td>;
    return (
      <td key={i} className={cls} title={`plan / remaining = plan − actual sales through yesterday in the forecast weeks (${fmt(Math.round(act))} sold)`}>
        {fmt(Math.round(v))} / <span className={Math.round(rem) < 0 ? 'pp-rem-neg' : undefined}>{fmt(Math.round(rem) || 0)}</span>
      </td>
    );
  };
  const stockTd = (i, v) => <td key={i}>{num(v) > 0 ? fmt(num(v)) : '—'}</td>;

  return (
    <tr>
      {cols.map((c, i) => {
        let v, dm;
        const zc = c.cls || '';                                   // zone tint / separator classes
        const cx = (...a) => [zc, ...a].filter(Boolean).join(' ');
        switch (c.k) {
          case 'asin': return <td key={i}>{p.ASIN || '—'}</td>;
          case 'sku': return <td key={i}>{p.SKU}</td>;
          case 'name': return <td key={i} className="pp-name" title={p.name}>{p.name}</td>;
          case 'bpb': return <td key={i} className="pp-box">{p.bagsPerBox || '—'}</td>;
          case 'pps': return <td key={i}>{p.produce_per_shift == null ? '—' : fmt(num(p.produce_per_shift))}</td>;
          case 'wh': return stockTd(i, p.stock_wh);
          case 'fba': return stockTd(i, p.stock_fba);
          case 'fbaA': return stockTd(i, p.stock_fba_available);
          case 'fbaI': return stockTd(i, p.stock_fba_inbound);
          case 'awd': return stockTd(i, p.stock_awd);
          case 'awdA': return stockTd(i, p.stock_awd_available);
          case 'awdI': return stockTd(i, p.stock_awd_inbound);
          case 'ai': return aiTd(i, fc.aiByMonth[c.m], fc.remByMonth[c.m], fc.elapsedByMonth[c.m], fc.actByMonth[c.m], zc);
          case 'aiw': return aiTd(i, fc.shareBySeg[c.segKey], fc.remBySeg[c.segKey], fc.elapsedBySeg[c.segKey], fc.actBySeg[c.segKey], zc);
          case 'dm': return numTd(i, fc.dmByMonth[c.m], zc);
          case 'dmw': return numTd(i, fc.dmBySeg[c.segKey], zc);
          case 'mp': return numTd(i, cumPlan[c.m], zc);
          case 'md': return numTd(i, agg.md[c.m] || 0, cx('pp-done'));
          case 'mdp': dm = fc.dmByMonth[c.m]; return dm == null ? <td key={i} className={zc}>—</td> : numTd(i, (cumPlan[c.m] || 0) + dm, zc);   // also the Demand zone in Planner View (month may lie outside the Days range → cumPlan 0)
          case 'mdf': {   // Production Gap 'if nothing is produced / with the current plan' (gapValues, horizon from the header box); negative = not enough, red bg by the planned value
            const g = gapValues(fc, cumPlan, c.m);
            if (!g) return <td key={i} className={zc}>—</td>;
            const gapSpan = x => <span className={Math.round(x) < 0 ? 'pp-rem-neg' : undefined}>{fmt(Math.round(x) || 0)}</span>;
            return (
              <td key={i} className={Math.round(g.plan) < 0 ? cx('pp-neg') : zc} title={`${g.days}-day gap: if nothing is produced / with the current plan = Demand Plan − forecast of the ${g.days} days after the month; negative = not enough`}>
                {gapSpan(g.noProd)} / {gapSpan(g.plan)}
              </td>
            );
          }
          case 'wp': v = agg.wp[c.wk]; return <td key={i} className={zc} title="Expand the week to edit">{v ? fmt(v) : '—'}</td>;
          case 'wd': v = agg.wd[c.wk]; return <td key={i} className={cx('pp-done')}>{v ? fmt(v) : '—'}</td>;
          case 'dd': {   // Done cell carries the day comment (the team comments on what was done)
            v = num(doneRow && doneRow[c.d]);
            const cm = commentRow && commentRow[c.d];
            return (
              <td key={i} className={'pp-done pp-dc ' + c.cls} title={cm || undefined}>
                {cm && <span className="pp-dot" title={cm} />}
                <i className="fas fa-comment-dots pp-cbtn" title={cm ? 'Edit comment' : 'Add comment'} onClick={e => { e.stopPropagation(); onComment(p.SKU, c.d, e.currentTarget.closest('td')); }} />
                {v ? fmt(v) : '—'}
              </td>
            );
          }
          case 'dp': {
            v = num(planRow && planRow[c.d]);
            const isEd = editing && editing.date === c.d;
            return (
              <td key={i} className={'pp-dp ' + c.cls + (isEd ? ' pp-editing' : '')} onClick={isEd ? undefined : () => onEdit(p.SKU, c.d)} title={isEd ? undefined : 'Click to edit'}>
                {isEd ? <EditInput initial={v ? String(v) : ''} onDone={(val, move) => onEditDone(p.SKU, c.d, val, move)} /> : (v ? fmt(v) : '—')}
              </td>
            );
          }
          default: return <td key={i} />;
        }
      })}
    </tr>
  );
});
